const TERMINAL_ATTEMPT_STATUSES = new Set([
  "COMPLETE",
  "COMPLETE_WITH_ERRORS",
  "COMPLETE_WITH_TAIL",
  "FAILED",
  "FAILED_MISMATCH",
  "BLOCKED_NO_CANDIDATES",
  "CANCELLED",
  "PARTIAL",
  "PAUSED_BY_ERROR",
  "SKIPPED",
  "STOPPED",
]);

export function normalizeAttemptStatus(status: string | null | undefined): string {
  return String(status ?? "").trim().toUpperCase();
}

export function isTerminalAttemptStatus(status: string | null | undefined): boolean {
  const normalized = normalizeAttemptStatus(status);
  return normalized.length > 0 && TERMINAL_ATTEMPT_STATUSES.has(normalized);
}

export function getEffectiveAttemptStatus(row: {
  status?: string | null;
  finished_at?: string | null;
  diagnostics_json?: unknown;
} | null | undefined): string {
  const rawStatus = normalizeAttemptStatus(row?.status);
  const diag = (row?.diagnostics_json ?? null) as Record<string, unknown> | null;
  const diagStatus = normalizeAttemptStatus(typeof diag?.status === "string" ? diag.status : null);
  const statusGuardInput = normalizeAttemptStatus(
    typeof (diag?.status_guard as Record<string, unknown> | undefined)?.input_status === "string"
      ? ((diag?.status_guard as Record<string, unknown>).input_status as string)
      : null,
  );
  const endedAt = typeof diag?.ended_at === "string" ? diag.ended_at : null;
  const finishedAt = typeof row?.finished_at === "string" ? row.finished_at : null;

  // If the persisted attempt row lags but diagnostics are terminal, trust diagnostics.
  if (
    (rawStatus === "RUNNING" || rawStatus === "IN_PROGRESS") &&
    isTerminalAttemptStatus(diagStatus) &&
    (endedAt || finishedAt)
  ) {
    return diagStatus;
  }

  // If storage-constrained status remapping collapsed a richer terminal semantic
  // (e.g. COMPLETE_WITH_TAIL -> PARTIAL), expose the semantic input status.
  if (
    rawStatus === "PARTIAL" &&
    (statusGuardInput === "COMPLETE_WITH_ERRORS" || statusGuardInput === "COMPLETE_WITH_TAIL")
  ) {
    return statusGuardInput;
  }

  return rawStatus;
}

export function toStepRunningStatus(status: string | null | undefined): boolean {
  const normalized = normalizeAttemptStatus(status);
  return normalized === "RUNNING" || normalized === "IN_PROGRESS" || normalized === "QUEUED" || normalized === "ENQUEUED" || normalized === "PENDING";
}
